/**
 * Client-side clothing similarity using MobileNetV3-Small via ONNX Runtime Web.
 *
 * Model: almogtavor/vestir-clothing-similarity (INT8, 1.2MB)
 * Produces 128-dim L2-normalized embeddings that capture visual similarity
 * (texture, pattern, shape, color) between clothing items.
 *
 * v2: Triplet loss + hard negatives for better shape discrimination
 * Accuracy: 88.9% on hard-negative pairs (optimal threshold: 0.78)
 */

import * as ort from 'onnxruntime-web';
import { fetchHFModel } from './hfModelLoader';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const MODEL_URL =
  'https://huggingface.co/almogtavor/vestir-clothing-similarity/resolve/main/model_int8.onnx';
const SIMILARITY_THRESHOLD = 0.78;
const IMG_SIZE = 224;

// ImageNet normalization
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

// ---------------------------------------------------------------------------
// Singleton session
// ---------------------------------------------------------------------------

let _session: ort.InferenceSession | null = null;
let _sessionLoading: Promise<ort.InferenceSession> | null = null;

async function getSession(): Promise<ort.InferenceSession> {
  if (_session) return _session;
  if (_sessionLoading) return _sessionLoading;

  _sessionLoading = (async () => {
    try {
      const modelBuffer = await fetchHFModel(MODEL_URL);
      const session = await ort.InferenceSession.create(modelBuffer, {
        executionProviders: ['wasm'],
      });
      _session = session;
      return session;
    } catch (err) {
      _sessionLoading = null;
      throw err;
    }
  })();

  return _sessionLoading;
}

/** Pre-load the model (call on app startup). */
export async function warmUpSimilarity(): Promise<void> {
  await getSession();
}

// ---------------------------------------------------------------------------
// Image preprocessing
// ---------------------------------------------------------------------------

function imageToTensor(imageData: ImageData): ort.Tensor {
  const { data, width, height } = imageData;
  const float32 = new Float32Array(3 * IMG_SIZE * IMG_SIZE);

  for (let y = 0; y < IMG_SIZE; y++) {
    for (let x = 0; x < IMG_SIZE; x++) {
      const srcIdx = (y * width + x) * 4;
      const dstIdx = y * IMG_SIZE + x;

      // Normalize to [0,1] then apply ImageNet normalization
      float32[0 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx] / 255 - MEAN[0]) / STD[0];
      float32[1 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx + 1] / 255 - MEAN[1]) / STD[1];
      float32[2 * IMG_SIZE * IMG_SIZE + dstIdx] = (data[srcIdx + 2] / 255 - MEAN[2]) / STD[2];
    }
  }

  return new ort.Tensor('float32', float32, [1, 3, IMG_SIZE, IMG_SIZE]);
}

async function loadImageData(src: string | HTMLImageElement): Promise<ImageData> {
  let img: HTMLImageElement;
  if (typeof src === 'string') {
    img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = src;
    });
  } else {
    img = src;
  }

  const canvas = document.createElement('canvas');
  canvas.width = IMG_SIZE;
  canvas.height = IMG_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, IMG_SIZE, IMG_SIZE);
  return ctx.getImageData(0, 0, IMG_SIZE, IMG_SIZE);
}

// ---------------------------------------------------------------------------
// Embedding extraction
// ---------------------------------------------------------------------------

/** Extract a 128-dim embedding for a garment image. */
export async function getEmbedding(
  imageSource: string | HTMLImageElement,
): Promise<Float32Array> {
  const session = await getSession();
  const imageData = await loadImageData(imageSource);
  const tensor = imageToTensor(imageData);

  const results = await session.run({ pixel_values: tensor });
  const embedding = results.embedding.data as Float32Array;
  return embedding;
}

/** Compute cosine similarity between two embeddings. */
export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  return dot / (Math.sqrt(normA) * Math.sqrt(normB) + 1e-8);
}

/** Check if two garment images are visually similar. */
export async function areSimilar(
  img1: string | HTMLImageElement,
  img2: string | HTMLImageElement,
  threshold = SIMILARITY_THRESHOLD,
): Promise<{ similar: boolean; score: number }> {
  const [emb1, emb2] = await Promise.all([getEmbedding(img1), getEmbedding(img2)]);
  const score = cosineSimilarity(emb1, emb2);
  return { similar: score >= threshold, score };
}

export { SIMILARITY_THRESHOLD };
